/** Road carriageway / lane direction helpers — see docs/data-collection/ROAD_LANE_DIRECTION_POLICY.md */

import type { OuterKerbReferenceEdge, StripCaptureMode } from '../../utils/roadStripSurvey';
import { DEFAULT_OUTER_KERB_REFERENCE } from '../../utils/roadStripSurvey';
import {
  dividedNeedsDivideTrace,
  parseDivideWidthM,
  stripNeedsMapTrace,
} from '../../utils/roadStripSurvey';

export type LaneDirection = 'forward' | 'backward' | 'both';

/** Sum of per-lane widths must match strip width within this tolerance (policy §2.14). */
export const LANE_WIDTH_SUM_TOLERANCE_M = 0.1;

export const LANE_DIRECTION_OPTIONS: { value: LaneDirection; label: string }[] = [
  { value: 'forward', label: 'Forward' },
  { value: 'backward', label: 'Backward' },
  { value: 'both', label: 'Both ways' },
];

export function isMultiCarriageway(carriagewayCount: number | null): boolean {
  return carriagewayCount != null && carriagewayCount >= 2;
}

export function parseLaneCount(raw: string): number | null {
  const n = parseInt(raw.trim(), 10);
  if (Number.isNaN(n) || n < 1 || n > 12) return null;
  return n;
}

/** Forward/backward counts allow 0 (e.g. one-way split not used). */
export function parseOptionalWidthM(raw: string): number | null {
  if (!raw.trim()) return null;
  const n = parseFloat(raw.trim());
  if (Number.isNaN(n) || n <= 0 || n > 60) return null;
  return n;
}

export function parseDirectionalLaneCount(raw: string): number | null {
  if (!raw.trim()) return null;
  const n = parseInt(raw.trim(), 10);
  if (Number.isNaN(n) || n < 0 || n > 8) return null;
  return n;
}

export function expectedManualLaneCount(payload: {
  carriagewayCount: number | null;
  lanes: string;
  lanesForward: string;
  lanesBackward: string;
}): number | null {
  const total = parseLaneCount(payload.lanes);
  if (total != null) return total;

  const forward = parseDirectionalLaneCount(payload.lanesForward);
  const backward = parseDirectionalLaneCount(payload.lanesBackward);
  if (forward != null && backward != null && forward + backward > 0) {
    return forward + backward;
  }
  return null;
}

export function resizeLaneWidthStrings(widths: string[], count: number): string[] {
  if (count < 1) return [];
  if (widths.length === count) return widths;
  if (widths.length > count) return widths.slice(0, count);
  return [...widths, ...Array.from({ length: count - widths.length }, () => '')];
}

export function parseLaneWidthM(raw: string): number | null {
  if (!raw.trim()) return null;
  const n = parseFloat(raw.trim());
  if (Number.isNaN(n) || n <= 0 || n > 20) return null;
  return n;
}

export function laneWidthsToPipe(widths: string[]): string | null {
  const parsed = widths.map(parseLaneWidthM);
  if (parsed.some((w) => w == null)) return null;
  return parsed.map((w) => String(w)).join('|');
}

export function parseLaneWidthsPipe(raw: unknown): string[] {
  if (typeof raw !== 'string' || !raw.trim()) return [];
  return raw.split('|').map((p) => p.trim());
}

export function validatePerLaneWidths(
  laneCount: number,
  stripWidthM: number | null,
  widthStrings: string[],
): string | null {
  if (laneCount < 1) return null;
  if (widthStrings.length !== laneCount) {
    return `Enter a width (metres) for each of the ${laneCount} lane${laneCount === 1 ? '' : 's'}.`;
  }
  const parsed = widthStrings.map(parseLaneWidthM);
  if (parsed.some((w) => w == null)) {
    return 'Each lane width must be a positive number up to 20 m.';
  }
  const widths = parsed as number[];
  const sum = widths.reduce((acc, w) => acc + w, 0);
  if (stripWidthM != null && Math.abs(sum - stripWidthM) > LANE_WIDTH_SUM_TOLERANCE_M) {
    return `Lane widths must add up to ${stripWidthM} m (±${LANE_WIDTH_SUM_TOLERANCE_M} m). Current sum: ${sum.toFixed(1)} m.`;
  }
  return null;
}

export function resizeLaneDirections(directions: LaneDirection[], count: number): LaneDirection[] {
  if (count < 1) return [];
  if (directions.length === count) return [...directions];
  if (directions.length > count) return directions.slice(0, count);
  return [
    ...directions,
    ...Array.from({ length: count - directions.length }, () => 'forward' as const),
  ];
}

export function validateRoadFlowFields(payload: {
  carriagewayCount: number | null;
  lanes: string;
  lanesForward: string;
  lanesBackward: string;
  laneDirections: LaneDirection[];
  manualLaneDirections: boolean;
  roadWidth?: string;
  dividedCarriagewayBReady?: boolean;
  dividedDivideReady?: boolean;
  divideWidthM?: string;
  singleStripCaptureMode?: StripCaptureMode;
  stripACaptureMode?: StripCaptureMode;
  stripBCaptureMode?: StripCaptureMode;
  singleStripTraceReady?: boolean;
  stripATraceReady?: boolean;
  stripBTraceReady?: boolean;
  carriagewayAWidth?: string;
  carriagewayBWidth?: string;
  carriagewayALaneDirections?: LaneDirection[];
  carriagewayBLaneDirections?: LaneDirection[];
  manualDividedLaneDirections?: boolean;
  usePerLaneWidths?: boolean;
  laneWidthsM?: string[];
  carriagewayALaneWidthsM?: string[];
  carriagewayBLaneWidthsM?: string[];
}): string | null {
  if (payload.carriagewayCount == null || payload.carriagewayCount < 1) {
    return 'Select how many carriageways this road has before continuing.';
  }

  if (payload.lanes.trim() && parseLaneCount(payload.lanes) == null) {
    return 'Total lanes must be a whole number from 1 to 12.';
  }
  if (payload.lanesForward.trim() && parseDirectionalLaneCount(payload.lanesForward) == null) {
    return 'Forward lanes must be a whole number from 0 to 8.';
  }
  if (payload.lanesBackward.trim() && parseDirectionalLaneCount(payload.lanesBackward) == null) {
    return 'Backward lanes must be a whole number from 0 to 8.';
  }

  const total = parseLaneCount(payload.lanes);
  const forward = parseDirectionalLaneCount(payload.lanesForward);
  const backward = parseDirectionalLaneCount(payload.lanesBackward);

  if (isMultiCarriageway(payload.carriagewayCount)) {
    const modeA = payload.stripACaptureMode ?? 'derive_from_divide';
    const modeB = payload.stripBCaptureMode ?? 'derive_from_divide';
    if (dividedNeedsDivideTrace(modeA, modeB) && payload.dividedDivideReady === false) {
      return 'Trace the divide (seam or median centre) before continuing.';
    }
    const aWidth = parseOptionalWidthM(payload.carriagewayAWidth ?? '');
    const bWidth = parseOptionalWidthM(payload.carriagewayBWidth ?? '');
    if (aWidth == null || bWidth == null) {
      return 'Enter paved widths (metres) for carriageways A and B.';
    }
    if (stripNeedsMapTrace(modeA) && payload.stripATraceReady === false) {
      return 'Trace carriageway A on the map (centre or outer kerb per your capture mode).';
    }
    if (stripNeedsMapTrace(modeB) && payload.stripBTraceReady === false) {
      return 'Trace carriageway B on the map, or tap “Trace carriageway B”.';
    }
    if (
      payload.dividedCarriagewayBReady === false
      && !stripNeedsMapTrace(modeA)
      && !stripNeedsMapTrace(modeB)
    ) {
      return 'Carriageway geometry is incomplete — check divide trace, widths, and strip captures.';
    }
    const aLanes = parseDirectionalLaneCount(payload.lanesForward);
    const bLanes = parseDirectionalLaneCount(payload.lanesBackward);
    if (aLanes == null || bLanes == null) {
      return 'Enter lane counts for carriageway A and carriageway B.';
    }
    if (aLanes + bLanes < 1) {
      return 'Enter at least one lane on each divided carriageway.';
    }
    const aDirs = payload.carriagewayALaneDirections ?? [];
    const bDirs = payload.carriagewayBLaneDirections ?? [];
    if (aDirs.length !== aLanes) {
      return `Set exactly ${aLanes} lane direction${aLanes === 1 ? '' : 's'} for carriageway A.`;
    }
    if (bDirs.length !== bLanes) {
      return `Set exactly ${bLanes} lane direction${bLanes === 1 ? '' : 's'} for carriageway B.`;
    }
    if (payload.usePerLaneWidths) {
      const aErr = validatePerLaneWidths(aLanes, aWidth, payload.carriagewayALaneWidthsM ?? []);
      if (aErr) return `Carriageway A: ${aErr}`;
      const bErr = validatePerLaneWidths(bLanes, bWidth, payload.carriagewayBLaneWidthsM ?? []);
      if (bErr) return `Carriageway B: ${bErr}`;
    }
    return null;
  }

  const singleMode = payload.singleStripCaptureMode ?? 'trace_centerline';
  if (parseOptionalWidthM(payload.roadWidth ?? '') == null) {
    return 'Enter road width (metres) for this carriageway.';
  }
  if (stripNeedsMapTrace(singleMode) && payload.singleStripTraceReady === false) {
    return 'Trace this carriageway on the map before continuing.';
  }

  if (total != null && forward != null && backward != null) {
    const bothCount = payload.laneDirections.filter((d) => d === 'both').length;
    if (forward + backward + bothCount !== total) {
      return bothCount > 0
        ? 'Forward + backward lanes plus any both-way lanes must equal total lanes.'
        : 'Forward and backward lane counts must add up to total lanes.';
    }
  }

  const expected =
    expectedManualLaneCount({
      carriagewayCount: payload.carriagewayCount,
      lanes: payload.lanes,
      lanesForward: payload.lanesForward,
      lanesBackward: payload.lanesBackward,
    }) ?? 1;

  let directions = payload.laneDirections;
  if (directions.length === 0) {
    directions = buildLaneDirectionsFromCounts(total, forward, backward);
  }
  if (directions.length !== expected) {
    return `Set exactly ${expected} lane direction${expected === 1 ? '' : 's'} (L1 to L${expected}).`;
  }

  const counts = countLaneDirections(directions);
  if (forward != null && counts.forward !== forward) {
    return `Lane directions have ${counts.forward} forward lane(s); forward lanes is ${forward}.`;
  }
  if (backward != null && counts.backward !== backward) {
    return `Lane directions have ${counts.backward} backward lane(s); backward lanes is ${backward}.`;
  }

  if (payload.usePerLaneWidths) {
    const stripW = parseOptionalWidthM(payload.roadWidth ?? '');
    const widthErr = validatePerLaneWidths(expected, stripW, payload.laneWidthsM ?? []);
    if (widthErr) return widthErr;
  }

  return null;
}

export function buildLaneDirectionsFromCounts(
  totalLanes: number | null,
  forward: number | null,
  backward: number | null,
): LaneDirection[] {
  if (forward != null || backward != null) {
    const f = Math.max(0, forward ?? 0);
    const b = Math.max(0, backward ?? 0);
    if (f + b === 0) return [];
    return [...Array.from({ length: f }, () => 'forward' as const), ...Array.from({ length: b }, () => 'backward' as const)];
  }
  if (totalLanes != null && totalLanes >= 2) {
    const half = Math.floor(totalLanes / 2);
    const rest = totalLanes - half;
    return [...Array.from({ length: half }, () => 'forward' as const), ...Array.from({ length: rest }, () => 'backward' as const)];
  }
  if (totalLanes === 1) {
    return ['forward'];
  }
  return [];
}

export function buildDividedCarriagewayLaneDirections(laneCount: number | null): LaneDirection[] {
  const n = laneCount ?? 0;
  if (n < 1) return [];
  return Array.from({ length: n }, () => 'forward');
}

export function laneDirectionsToString(directions: LaneDirection[]): string {
  return directions.filter(Boolean).join('|');
}

export function parseLaneDirections(raw: unknown): LaneDirection[] {
  if (typeof raw !== 'string' || !raw.trim()) return [];
  return raw.split('|').map((part) => {
    const v = part.trim().toLowerCase();
    if (v === 'forward' || v === 'backward' || v === 'both') return v;
    return null;
  }).filter((v): v is LaneDirection => v != null);
}

export function countLaneDirections(directions: LaneDirection[]): { forward: number; backward: number; both: number } {
  return directions.reduce(
    (acc, d) => {
      if (d === 'forward') acc.forward += 1;
      else if (d === 'backward') acc.backward += 1;
      else if (d === 'both') acc.both += 1;
      return acc;
    },
    { forward: 0, backward: 0, both: 0 },
  );
}

/** OSM-style directional lane counts: both-way lanes count toward forward and backward. */
export function directionalLaneCountsFromDirections(
  directions: LaneDirection[],
): { forward: number; backward: number } {
  const counts = countLaneDirections(directions);
  return {
    forward: counts.forward + counts.both,
    backward: counts.backward + counts.both,
  };
}

export function inferCarriagewayCountFromDetails(details: {
  carriageway_count?: number | null;
  divided_carriageway_pair?: boolean;
  parent_road_id?: string | null;
  /** @deprecated legacy contribution / tile field */
  carriageway_group_id?: string | null;
  /** @deprecated legacy contribution field */
  flow_mode?: string;
}): number {
  if (details.carriageway_count != null && details.carriageway_count >= 1) {
    return details.carriageway_count;
  }
  if (
    details.divided_carriageway_pair
    || details.parent_road_id
    || details.carriageway_group_id
    || details.flow_mode === 'divided'
  ) {
    return 2;
  }
  return 1;
}

export function roadFlowExtras(payload: {
  carriagewayCount: number | null;
  lanes: string;
  lanesForward: string;
  lanesBackward: string;
  laneDirections: LaneDirection[];
  manualLaneDirections: boolean;
  carriagewayALaneDirections?: LaneDirection[];
  carriagewayBLaneDirections?: LaneDirection[];
  manualDividedLaneDirections?: boolean;
  roadWidth?: string;
  carriagewayAWidth?: string;
  carriagewayBWidth?: string;
  divideGeometry?: GeoJSON.LineString | null;
  divideWidthM?: string;
  singleStripCaptureMode?: StripCaptureMode;
  stripACaptureMode?: StripCaptureMode;
  stripBCaptureMode?: StripCaptureMode;
  stripAReferenceGeometry?: GeoJSON.LineString | null;
  stripBReferenceGeometry?: GeoJSON.LineString | null;
  singleStripReferenceGeometry?: GeoJSON.LineString | null;
  singleStripOuterKerbReference?: OuterKerbReferenceEdge;
  usePerLaneWidths?: boolean;
  laneWidthsM?: string[];
  carriagewayALaneWidthsM?: string[];
  carriagewayBLaneWidthsM?: string[];
}): Record<string, unknown> {
  if (payload.carriagewayCount == null || payload.carriagewayCount < 1) return {};

  const total = parseLaneCount(payload.lanes);
  const forward = parseDirectionalLaneCount(payload.lanesForward);
  const backward = parseDirectionalLaneCount(payload.lanesBackward);

  const extra: Record<string, unknown> = {
    carriageway_count: payload.carriagewayCount,
    layout: isMultiCarriageway(payload.carriagewayCount) ? 'divided' : 'single',
  };

  if (isMultiCarriageway(payload.carriagewayCount)) {
    extra.divided_carriageway_pair = true;
    const modeA = payload.stripACaptureMode ?? 'derive_from_divide';
    const modeB = payload.stripBCaptureMode ?? 'derive_from_divide';
    extra.strip_a_capture_mode = modeA;
    extra.strip_b_capture_mode = modeB;
    const divideW = parseDivideWidthM(payload.divideWidthM ?? 0);
    if (divideW > 0) extra.divide_width_m = divideW;
    if (payload.divideGeometry) extra.divide_geometry = payload.divideGeometry;
    if (payload.stripAReferenceGeometry) extra.strip_a_reference_geometry = payload.stripAReferenceGeometry;
    if (payload.stripBReferenceGeometry) extra.strip_b_reference_geometry = payload.stripBReferenceGeometry;
    if (forward != null) extra.carriageway_a_lanes = forward;
    if (backward != null) extra.carriageway_b_lanes = backward;

    const aDirs = payload.carriagewayALaneDirections ?? [];
    const bDirs = payload.carriagewayBLaneDirections ?? [];
    const aLaneDirectionsStr = laneDirectionsToString(aDirs);
    const bLaneDirectionsStr = laneDirectionsToString(bDirs);
    if (aLaneDirectionsStr) extra.carriageway_a_lane_directions = aLaneDirectionsStr;
    if (bLaneDirectionsStr) extra.carriageway_b_lane_directions = bLaneDirectionsStr;
    if (aDirs.length > 0) {
      const aCounts = directionalLaneCountsFromDirections(aDirs);
      extra.carriageway_a_lanes_forward = aCounts.forward;
      extra.carriageway_a_lanes_backward = aCounts.backward;
    }
    if (bDirs.length > 0) {
      const bCounts = directionalLaneCountsFromDirections(bDirs);
      extra.carriageway_b_lanes_forward = bCounts.forward;
      extra.carriageway_b_lanes_backward = bCounts.backward;
    }
    const aWidth = parseOptionalWidthM(payload.carriagewayAWidth ?? '');
    const bWidth = parseOptionalWidthM(payload.carriagewayBWidth ?? '');
    if (aWidth != null) extra.carriageway_a_width = aWidth;
    if (bWidth != null) extra.carriageway_b_width = bWidth;
    if (payload.usePerLaneWidths) {
      const aPipe = laneWidthsToPipe(payload.carriagewayALaneWidthsM ?? []);
      const bPipe = laneWidthsToPipe(payload.carriagewayBLaneWidthsM ?? []);
      if (aPipe) extra.carriageway_a_lane_widths = aPipe;
      if (bPipe) extra.carriageway_b_lane_widths = bPipe;
    }
    return extra;
  }

  let directions = payload.laneDirections;
  if (directions.length === 0) {
    directions = buildLaneDirectionsFromCounts(total, forward, backward);
  }
  if (directions.length === 0) {
    directions = ['forward'];
  }

  if (total != null) extra.lanes = total;
  else if (directions.length === 1) extra.lanes = 1;

  const { forward: fwdCount, backward: bwdCount } = directionalLaneCountsFromDirections(directions);
  extra.lanes_forward = fwdCount;
  extra.lanes_backward = bwdCount;

  const laneDirectionsStr = laneDirectionsToString(directions);
  if (laneDirectionsStr) extra.lane_directions = laneDirectionsStr;

  const width = parseOptionalWidthM(payload.roadWidth ?? '');
  if (width != null) extra.width = width;

  const singleMode = payload.singleStripCaptureMode ?? 'trace_centerline';
  extra.strip_capture_mode = singleMode;
  if (singleMode === 'trace_outer_kerb') {
    extra.outer_kerb_reference =
      payload.singleStripOuterKerbReference ?? DEFAULT_OUTER_KERB_REFERENCE;
  }
  if (payload.singleStripReferenceGeometry) {
    extra.strip_reference_geometry = payload.singleStripReferenceGeometry;
  }

  if (payload.usePerLaneWidths) {
    const pipe = laneWidthsToPipe(payload.laneWidthsM ?? []);
    if (pipe) extra.lane_widths = pipe;
  }

  return extra;
}
